import { useId, type ReactNode } from 'react'
import { ChevronDownIcon } from '@/components/Icons'

export interface SelectFieldProps {
  /** Usually a string, but several of these labels carry a leading icon. */
  label: ReactNode
  /** Optional control rendered beside (not inside) the native label. */
  labelAccessory?: ReactNode
  value: string
  onChange: (value: string) => void
  /** Rendered inside the native `select`; normally a list of `<option>`. */
  children: ReactNode
  id?: string
  disabled?: boolean
  testId?: string
  /**
   * Keeps the label for screen readers but takes it out of the layout, for the
   * filter rows where the options already say what the control is for.
   */
  labelHidden?: boolean
  /**
   * Native tooltip on the control, for a value the field is too narrow to show
   * whole. A `select` clips its own text with nothing to say it did (BRAI-703).
   */
  title?: string
  /**
   * `sm` is for narrow side panels, where the form-sized field towers over the
   * 11–13px controls around it.
   */
  size?: 'md' | 'sm'
}

const LABEL_CLASS = {
  md: 'mb-1 block text-xs font-semibold text-[var(--text-secondary)] sm:mb-1.5',
  sm: 'mb-1 block text-[11px] font-semibold text-[var(--text-secondary)]',
} as const

const SELECT_CLASS = {
  md: 'input-field appearance-none pr-10 disabled:cursor-not-allowed disabled:opacity-60',
  sm: 'input-field appearance-none rounded-lg py-1.5 pl-2.5 pr-8 text-xs disabled:cursor-not-allowed disabled:opacity-60 sm:text-xs',
} as const

const CHEVRON_CLASS = {
  md: 'pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-secondary)]',
  sm: 'pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-secondary)]',
} as const

/**
 * The one dropdown look in this app, over a native `select`.
 *
 * Every field here used to set `appearance-none` and then draw nothing, so the
 * browser's own arrow was removed and not replaced: six controls that read as
 * disabled text boxes with no sign they could be opened at all.
 *
 * The arrow is drawn rather than left to the browser because a native one sits
 * hard against the edge, while `input-field` pads its text by 14px — the field
 * would look lopsided beside the others in the same form.
 *
 * Stays a native `select` on purpose. For a handful of options that is the
 * control with the better keyboard handling, the better screen-reader support
 * and, on a phone, the platform's own picker. `Combobox` carries the same look
 * for the lists long enough to need searching.
 *
 * Only the label and the control: any hint, error or checkbox a field carries
 * stays where it was, as a sibling. Pulling those in would have meant a slot
 * per field and a component shaped by its most complicated caller.
 */
export default function SelectField({
  label,
  labelAccessory,
  value,
  onChange,
  children,
  id,
  disabled = false,
  testId,
  labelHidden = false,
  title,
  size = 'md',
}: SelectFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <div>
      {labelAccessory && !labelHidden ? (
        <div className="flex items-start">
          <label htmlFor={fieldId} className={LABEL_CLASS[size]}>
            {label}
          </label>
          {labelAccessory}
        </div>
      ) : (
        <label
          htmlFor={fieldId}
          className={labelHidden ? 'sr-only' : LABEL_CLASS[size]}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={fieldId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          title={title}
          data-testid={testId}
          className={SELECT_CLASS[size]}
        >
          {children}
        </select>
        <ChevronDownIcon className={CHEVRON_CLASS[size]} />
      </div>
    </div>
  )
}
